import { Children, ReactNode } from "react";

import styles from "./AlertStack.module.css";

interface Props {
  children?: ReactNode;
  /**
   * Extra class on the stack itself — this is how a caller adds the outer
   * rhythm the stack deliberately does not own (Bots passes its
   * `margin-bottom: 16px`). Same escape hatch as Checkbox and Sparkline.
   */
  className?: string;
}

/**
 * AlertStack — several `<Alert>`s one under another, 16px apart.
 *
 * Alerts carry no margins of their own, so before this every page that showed
 * more than one banner invented the gap again, and the Bots Overview tab
 * forgot to: its four banners shared a border. The rule now lives in one
 * place. The gap is fixed at `--space-4` — the value the Bots mockup uses
 * (bots-page.html L115) and the rhythm the rest of the admin is built on.
 *
 * Nothing outside is the stack's business: no margins here, the caller sets
 * them. An empty stack renders nothing at all, so a caller can hand it a row
 * of conditions without guarding the whole block — with no banner to show
 * there is no empty div left behind to eat the surrounding spacing.
 */
export function AlertStack({ children, className }: Props) {
  // Drops the `false` of `{cond && <Alert/>}` and flattens `.map()` results,
  // so "nothing to show" is length 0 rather than a list of falsy holes.
  if (Children.toArray(children).length === 0) return null;

  return (
    <div className={`${styles.stack}${className ? ` ${className}` : ""}`}>
      {children}
    </div>
  );
}
